'use client';

import Link from '@/components/NoPrefetchLink';
import { useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useEffect } from 'react';
import { withLocalePrefix } from '@/lib/localePath';
import type { Locale } from '@/lib/localePath';
import '@/styles/Landing.css';

type Props = { locale: Locale };

type LandingItem = {
  href: string;
  title: { ja: string; en: string; es: string };
};

type LeagueSection = {
  key: 'mlb' | 'npb' | 'international';
  title: { ja: string; en: string; es: string };
  items: LandingItem[];
};

const LEAGUE_SECTIONS: LeagueSection[] = [
  {
    key: 'mlb',
    title: {
      ja: 'メジャーリーグ（MLB）',
      en: 'Major League Baseball (MLB)',
      es: 'Grandes Ligas (MLB)',
    },
    items: [
      {
        href: '/mlb',
        title: { ja: 'チーム', en: 'Teams', es: 'Equipos' },
      },
      {
        href: '/mlb-career',
        title: { ja: '通算記録', en: 'Career Records', es: 'Récords de carrera' },
      },
      {
        href: '/mlb-season',
        title: { ja: 'シーズン記録', en: 'Season Records', es: 'Récords de temporada' },
      },
      {
        href: '/mlb-leader',
        title: { ja: '年度別リーダー', en: 'Year-by-Year Leaders', es: 'Líderes por año' },
      },
      {
        href: '/awards',
        title: { ja: 'アワード', en: 'Awards', es: 'Premios' },
      },
    ],
  },
  {
    key: 'npb',
    title: {
      ja: 'プロ野球（NPB）',
      en: 'Nippon Professional Baseball (NPB)',
      es: 'Béisbol Profesional Japonés (NPB)',
    },
    items: [
      {
        href: '/npb',
        title: { ja: 'チーム', en: 'Teams', es: 'Equipos' },
      },
      {
        href: '/npb-career',
        title: { ja: '通算記録', en: 'Career Records', es: 'Récords de carrera' },
      },
      {
        href: '/npb-season',
        title: { ja: 'シーズン記録', en: 'Season Records', es: 'Récords de temporada' },
      },
      {
        href: '/npb-leader',
        title: { ja: '年度別リーダー', en: 'Year-by-Year Leaders', es: 'Líderes por año' },
      },
      {
        href: '/npb-awards',
        title: { ja: 'アワード', en: 'Awards', es: 'Premios' },
      },
    ],
  },
  {
    key: 'international',
    title: {
      ja: '国際大会',
      en: 'International',
      es: 'Internacional',
    },
    items: [
      {
        href: '/international/wbc',
        title: { ja: 'WBC', en: 'WBC', es: 'WBC' },
      },
      {
        href: '/international/premier12',
        title: { ja: 'プレミア12', en: 'Premier12', es: 'Premier12' },
      },
      {
        href: '/international/olympics',
        title: { ja: 'オリンピック', en: 'Olympics', es: 'Olímpicos' },
      },
    ],
  },
];

function LandingInner({ locale }: Props) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const t = (ja: string, en: string, es: string) => {
    if (locale === 'ja') return ja;
    if (locale === 'es') return es;
    return en;
  };

  useEffect(() => {
    const tab = searchParams?.get('tab') ?? null;
    if (tab === 'npb' || tab === 'mlb' || tab === 'players') {
      const next = new URLSearchParams(searchParams?.toString() ?? '');
      next.delete('tab');
      const qs = next.toString();
      router.replace(withLocalePrefix(locale, `/${tab}`) + (qs ? `?${qs}` : ''));
    }
  }, [searchParams, locale, router]);

  return (
    <div className="landing-page">
      <section className="landing-hero" aria-labelledby="landing-hero-title">
        <div className="landing-hero-content">
          <p className="landing-hero-eyebrow">
            {t(
              'MLB・NPB・国際大会 野球データ横断サイト',
              'A baseball data hub spanning MLB, NPB, and international tournaments',
              'Un portal de datos de béisbol que abarca MLB, NPB y torneos internacionales',
            )}
          </p>
          <h1 id="landing-hero-title">
            Baseball Data Viewer
          </h1>
        </div>
      </section>

      <div className="landing-sections" id="league-explorer">
        {LEAGUE_SECTIONS.map((section) => (
          <section key={section.key} className="landing-section">
            <div className="landing-section-heading">
              <h2 className="landing-section-title">{t(section.title.ja, section.title.en, section.title.es)}</h2>
            </div>
            <div className="landing-grid">
              {section.items.map((item) => (
                <Link key={`${section.key}-${item.href}`} href={withLocalePrefix(locale, item.href)} className="landing-card">
                  <h3>{t(item.title.ja, item.title.en, item.title.es)}</h3>
                  <span className="landing-card-arrow" aria-hidden="true">→</span>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

export default function LandingClient({ locale }: Props) {
  return (
    <Suspense>
      <LandingInner locale={locale} />
    </Suspense>
  );
}
